import { zodResolver } from '@hookform/resolvers/zod';
import { mt, rd } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { Shortlist } from '__types__';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { OptionRef } from 'v5/core/entity-values/values.types';
import { Dialog, DialogContentHeadless } from 'v5/design-system/Dialog';
import { Form } from 'v5/design-system/Form';
import { MountElementProps } from 'v5/messages/mount-element';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { FieldContextProvider } from 'v5/patterns/forms/schema-form/FieldContext';
import { TagsField } from 'v5/patterns/forms/schema-form/fields/TagsField';
import { TextField } from 'v5/patterns/forms/schema-form/fields/TextField';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { WithServices } from 'v5/platform/ts/services';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { useTagCreationAllowed } from 'v5/services/internal/SystemSettingsService/useTagCreationAllowed';
import { WithShortlistMutationService } from 'v5/services/io/ShortlistMutationService/ShortlistMutationService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';

type ShortlistCreateValues = {
  title: string;
  description: string;
  tags: OptionRef[];
};

export type ShortlistCreateDialogProps = MountElementProps &
  WithServices<
    [
      WithShortlistMutationService,
      WithTagService,
      WithAccessService,
      WithAppScopeService,
      WithSystemSettingsService
    ]
  > & {
    /** The opener's browsing scope; the new shortlist belongs to it. */
    enterpriseWorkspaceId: number | null;
    enterpriseTeamId: number | null;
    /** Side effects only (navigation); the dialog has closed by then. */
    onCreated(shortlist: Shortlist): void;
  };

/** Creates a shortlist with a title, description and tags in the opener's workspace and team. */
export function ShortlistCreateDialog({
  open,
  onOpenChange,
  ...contentProps
}: ShortlistCreateDialogProps) {
  return (
    <UnsavedChangesProtection open={open} onOpenChange={onOpenChange}>
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless className="max-w-120">
            <ShortlistCreateDialogContent
              open={dialogProps.open}
              {...contentProps}
            />
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}

function ShortlistCreateDialogContent({
  open,
  close,
  services,
  enterpriseWorkspaceId,
  enterpriseTeamId,
  onCreated
}: Omit<ShortlistCreateDialogProps, 'onOpenChange'>) {
  const { t } = useTranslation('shortlists', {
    keyPrefix: 'ShortlistCreateDialog'
  });
  const tags = rd.tryGet(
    services.tagsService.useTags({ type: 'shortlist', search: '' })
  );
  const tagCreationAllowed = useTagCreationAllowed(services);

  const form = useForm<ShortlistCreateValues>({
    defaultValues: { title: '', description: '', tags: [] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(
      z.object({
        title: z.string().trim().min(1, t('titleRequired')),
        description: z.string().trim(),
        tags: z.array(z.custom<OptionRef>())
      })
    )
  });
  useRegisterDirtyState(form.formState.isDirty);

  const mutation = promiseState.useMutation(
    async (values: ShortlistCreateValues) => {
      const shortlist = await services.shortlistMutationService.create({
        title: values.title,
        description: values.description || null,
        tags: values.tags,
        enterpriseWorkspaceId,
        enterpriseTeamId
      });
      // Saved: the way to the new shortlist must not ask to discard it.
      form.reset(values);
      close();
      onCreated(shortlist);
    }
  );

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        title={t('title')}
        description={t('description')}
        onSubmit={form.handleSubmit(values => {
          // The button only looks disabled: Enter and a second click land here.
          if (mt.isInProgress(mutation.state)) return;
          // A failure is shown above the button from the mutation's state.
          mutation.track(values).catch(() => {});
        })}
        mutation={mutation.state}
        saveText={t('create')}
      >
        <FieldContextProvider
          value={{
            mode: 'edit',
            variant: 'regular',
            size: 'md',
            basePath: '',
            ghost: false
          }}
        >
          <TextField
            spec={{
              key: 'title',
              label: t('titleLabel'),
              description: null,
              required: true,
              multiline: false,
              placeholder: t('titlePlaceholder')
            }}
            path="title"
          />
          <TextField
            spec={{
              key: 'description',
              label: t('descriptionLabel'),
              description: null,
              required: false,
              multiline: true,
              placeholder: t('descriptionPlaceholder')
            }}
            path="description"
          />
          <TagsField
            spec={{
              key: 'tags',
              label: t('tagsLabel'),
              description: null,
              required: false,
              allowCreate: tagCreationAllowed,
              options: (tags ?? []).map(tag => ({
                value: String(tag.id),
                label: tag.tag
              }))
            }}
            path="tags"
          />
        </FieldContextProvider>
      </FormDialogContent>
    </Form>
  );
}
